import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { CheckCircle, Home } from "lucide-react";
import { LanguageProvider } from "@/contexts/LanguageContext";

// Eagerly loaded components (above the fold / critical path)
import { Navigation } from "@/components/Navigation";
import { HeroSection } from "@/components/HeroSection";
import { VillasSection } from "@/components/VillasSection";
import { BookingModal } from "@/components/BookingModal";
import { WhatsAppButton } from "@/components/WhatsAppButton";

// Lazy loaded components (below the fold)
const AdventuresSection = lazy(() => import("@/components/AdventuresSection").then(m => ({ default: m.AdventuresSection })));
const AmenitiesSection = lazy(() => import("@/components/AmenitiesSection").then(m => ({ default: m.AmenitiesSection })));
const ReviewsSection = lazy(() => import("@/components/ReviewsSection").then(m => ({ default: m.ReviewsSection })));
const HouseRulesSection = lazy(() => import("@/components/HouseRulesSection").then(m => ({ default: m.HouseRulesSection })));
const LocationSection = lazy(() => import("@/components/LocationSection").then(m => ({ default: m.LocationSection })));
const FooterSection = lazy(() => import("@/components/FooterSection").then(m => ({ default: m.FooterSection })));

// Lazy load entire admin dashboard (biggest performance win)
const AdminLogin = lazy(() => import("@/components/AdminDashboard").then(m => ({ default: m.AdminLogin })));
const AdminDashboard = lazy(() => import("@/components/AdminDashboard").then(m => ({ default: m.AdminDashboard })));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Minimal loading fallback
const SectionLoader = () => (
  <div className="flex items-center justify-center py-20">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Home Page
const HomePage = () => {
  const [villas, setVillas] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedVilla, setSelectedVilla] = useState(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [villasRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/villas`),
          axios.get(`${API}/reviews`)
        ]);
        setVillas(villasRes.data);
        setReviews(reviewsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleBookVilla = (villa) => {
    setSelectedVilla(villa);
    setIsBookingModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-serif text-xl text-muted-foreground">Loading paradise...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navigation />
      <HeroSection />
      <VillasSection villas={villas} onBookVilla={handleBookVilla} />
      <Suspense fallback={<SectionLoader />}>
        <AdventuresSection />
        <AmenitiesSection />
        <ReviewsSection reviews={reviews} />
        <HouseRulesSection />
        <LocationSection />
        <FooterSection />
      </Suspense>
      <BookingModal villa={selectedVilla} isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} />
      <WhatsAppButton />
    </>
  );
};

// Booking Success Page
const BookingSuccessPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <Navigation isScrolled={true} />
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <div className="space-y-6">
            <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="font-serif text-3xl font-bold">Reservation Request Received!</h1>
            <p className="text-muted-foreground text-lg">Thank you for choosing Palmaréca Selah Village.</p>
            <div className="bg-muted/50 rounded-xl p-6 text-left space-y-4">
              <h3 className="font-semibold">What happens next?</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2"><CheckCircle size={16} className="mt-1 text-green-600 flex-shrink-0" /><span>We'll review your reservation request</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="mt-1 text-green-600 flex-shrink-0" /><span>We'll contact you via WhatsApp or email within 24 hours</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="mt-1 text-green-600 flex-shrink-0" /><span>We'll provide payment details to confirm your booking</span></li>
              </ul>
              <div className="pt-4 border-t">
                <p className="text-sm"><strong>Questions?</strong> Reach us at:</p>
                <p className="text-sm">WhatsApp: +1 829-455-1503</p>
                <p className="text-sm">Email: palmarecavillage@gmail.com</p>
              </div>
            </div>
            <Button onClick={() => navigate("/")} className="btn-accent">
              <Home size={16} className="mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Admin Page
const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (token) setIsAuthenticated(true);
  }, []);

  const handleLogin = () => setIsAuthenticated(true);
  const handleLogout = () => {
    setIsAuthenticated(false);
    navigate("/admin");
  };

  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-serif text-xl text-muted-foreground">Loading admin...</p>
        </div>
      </div>
    }>
      {!isAuthenticated ? (
        <AdminLogin onLogin={handleLogin} />
      ) : (
        <AdminDashboard onLogout={handleLogout} />
      )}
    </Suspense>
  );
};

// Main App
function App() {
  return (
    <LanguageProvider>
      <div className="min-h-screen">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/booking-success" element={<BookingSuccessPage />} />
            <Route path="/admin" element={<AdminPage />} />
          </Routes>
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
